import { SoftwareTag, VersionManagementGetFormDataResult } from "../VersionManagementQueries";
import { ReleaseFrequency, UpdateStrategy } from "@grabcad/company-server-shared/dist/types";
import { Company, Group, SoftwareTagWithUpdateStrategy } from "../types/version_management_types";

export function addUpdateStrategyToTags(
    softwareTags: SoftwareTag[]
): SoftwareTagWithUpdateStrategy[] {
    return softwareTags.map((tag) => ({
        ...tag,
        updateStrategy: getUpdateStrategyForTag(tag),
    }));
}

function getUpdateStrategyForTag(tag: SoftwareTag): UpdateStrategy {
    const isDefaultForReleaseFrequency = tag.isDefaultForReleaseFrequency;
    const isMonthly = tag.releaseFrequency === ReleaseFrequency.MONTHLY;
    const isQuarterly = tag.releaseFrequency === ReleaseFrequency.QUARTERLY;

    if (isMonthly && isDefaultForReleaseFrequency) {
        return UpdateStrategy.LatestRelease;
    }

    if (isQuarterly && isDefaultForReleaseFrequency) {
        return UpdateStrategy.LatestQuarterlyRelease;
    }

    return UpdateStrategy.LatestTaggedVersion;
}

export function mapQueryResultToVersionManagementState(data: VersionManagementGetFormDataResult): {
    softwareTags: SoftwareTagWithUpdateStrategy[];
    company: Company;
    groups: Group[];
} {
    const softwareTags = addUpdateStrategyToTags(data?.getSoftwareTags ?? []);

    /**
     * By default, a company won't have a tag, but we need to make it look like it does, so we pull
     * the monthly default one, if it exists.
     */
    let currentCompanyTagId = data?.getCompanySoftwareVersion?.tag?.id;
    if (!currentCompanyTagId) {
        currentCompanyTagId = softwareTags.find(
            (x) => x.releaseFrequency === ReleaseFrequency.MONTHLY && x.isDefaultForReleaseFrequency
        )?.id;
    }

    return {
        softwareTags,
        company: {
            name: data?.company.name,
            currentPrintVersion: data?.getSelectedPrintVersion?.productVersion,
            currentTagId: currentCompanyTagId,
            nextVersion: undefined,
        },
        groups: data?.company?.userGroups?.map((group) => ({
            id: group.id,
            name: group.name,
            currentVersion: group.softwareVersion?.selectedVersion?.productVersion,
            currentTagId: group.softwareVersion?.tag.id,
            nextVersion: undefined,
        })),
    };
}

export function shortSoftwareVersion(version?: string) {
    if (!version) {
        return "";
    }

    return version?.split(".").slice(0, 3).join(".") ?? "";
}
